import { Component, OnInit, Inject, signal, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { LatLng } from 'src/app/models/latlng';
import { TurnValueIntegerThousandsPipe } from 'src/app/pipes/turn-value-integer-thousands.pipe';
import { TurnValueIntegerPipe } from 'src/app/pipes/turn-value-integer.pipe';
import { ConsoleLoggerService } from 'src/app/services/logging/console-logger.service';
import { WaypointService } from 'src/app/services/core/waypoint.service';
import { VehicleService } from 'src/app/services/vehicle/vehicle.service';
import { LanguageService } from 'src/app/services/account/language.service';
import { AnalyticsLoggerService } from 'src/app/services/logging/analytics-logger.service';
import { NavigationService } from 'src/app/services/core/navigation.service';
import { SettingsService, Unit } from 'src/app/services/account/settings.service';
import { SpeechService, VoiceCommand } from 'src/app/services/media/speech.service';
import { UtilsService } from 'src/app/services/utils/utils.service';

const CONFIRMATION_STOP_RECOGNITION_TIMEOUT_MS: number = 5000;

@Component({
  standalone: true,
  selector: 'app-voice-command-confirmation-dialog',
  templateUrl: './voice-command-confirmation-dialog.component.html',
  imports: [TurnValueIntegerPipe, TurnValueIntegerThousandsPipe],
  styleUrls: ['./voice-command-confirmation-dialog.component.scss']
})

export class VoiceCommandConfirmationDialogComponent implements OnInit, OnDestroy {

  public readonly BatteryValue = signal<number>(0);
  public readonly WeightValue = signal<number>(0);
  public readonly EstimatedTime = signal<string>("0");
  public readonly RemainingDist = signal<number>(0);
  private SpeechRecognitionIsActive: boolean = false;
  private readonly Subscriptions: any[] = [];
  private readonly ConfirmationStopRecognitionMs: number = CONFIRMATION_STOP_RECOGNITION_TIMEOUT_MS;
  private CloseTimer: ReturnType<typeof setTimeout> = setTimeout(() => { }, 0);
  /** Set once the dialog is done (confirmed, timed out or cancelled), so the command can never run twice. */
  private Closed: boolean = false;

  constructor(
    private readonly SpeechRecognitionService: SpeechService,
    private readonly WaypointService: WaypointService,
    public dialogRef: MatDialogRef<VoiceCommandConfirmationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public languageService: LanguageService,
    public settingsService: SettingsService,
    private readonly consoleLoggerService: ConsoleLoggerService,
    private readonly navigationService: NavigationService,
    private readonly waypointService: WaypointService,
    private readonly utilsService: UtilsService,
    private readonly loggerService: AnalyticsLoggerService,
    private readonly vehicleService: VehicleService
  ) {
  }

  ngOnInit(): void {
    this.SpeechRecognitionIsActive = false;

    this.Subscriptions.push(this.SpeechRecognitionService.ObservableActiveVoiceCommands.subscribe((voiceCommands: VoiceCommand[] | null) => {
      if (voiceCommands != null) {
        if (!this.SpeechRecognitionIsActive) {
          this.SpeechRecognitionIsActive = voiceCommands.includes(VoiceCommand.Confirmation_Yes);
        }
        else if (voiceCommands.length == 0) {
          this.CloseDialog();
        }
      }
    }));

    switch (this.data.commandType) {
      case VoiceCommand.SetBattery:
        this.BatteryValue.set(this.data.batteryValue);
        this.SpeechRecognitionService.StartVoiceConfirmation(VoiceCommand.SetBattery, this.BatteryValue());
        break;
      case VoiceCommand.SkipWP:
        this.SpeechRecognitionService.StartVoiceConfirmation(VoiceCommand.SkipWP, null);
        break;
      case VoiceCommand.SkipCS:
        this.SpeechRecognitionService.StartVoiceConfirmation(VoiceCommand.SkipCS, null);
        break;
      case VoiceCommand.StopNavigation:
        this.SpeechRecognitionService.StartVoiceConfirmation(VoiceCommand.StopNavigation, null);
        break;
      case VoiceCommand.SetWeight:
        this.WeightValue.set(this.data.weightValue);
        this.SpeechRecognitionService.StartVoiceConfirmation(VoiceCommand.SetWeight, this.WeightValue());
        break;
      case VoiceCommand.NavigateToHome:
        this.SpeechRecognitionService.StartVoiceConfirmation(VoiceCommand.NavigateToHome, null);
        break;
      case VoiceCommand.NavigateToWork:
        this.SpeechRecognitionService.StartVoiceConfirmation(VoiceCommand.NavigateToWork, null);
        break;
      case VoiceCommand.ReadEte:
        this.EstimatedTime.set(this.utilsService.timeConverter(this.data.Ete));
        this.SpeechRecognitionService.StartVoiceConfirmation(VoiceCommand.ReadEte, this.data.Ete);
        break;
      case VoiceCommand.ReadEta:
        this.EstimatedTime.set(this.utilsService.timeConverter(this.data.Eta));
        this.SpeechRecognitionService.StartVoiceConfirmation(VoiceCommand.ReadEta, this.data.Eta);
        break;
      case VoiceCommand.ReadRemainingDist:
        this.RemainingDist.set(this.data.RemainingDist);
        this.SpeechRecognitionService.StartVoiceConfirmation(VoiceCommand.ReadRemainingDist, this.data.RemainingDist);
        break;
    }

    this.CloseTimer = setTimeout(() => {
      this.CloseDialog();
    }, this.ConfirmationStopRecognitionMs);
  }

  public Cancel(): void {
    // Before StopRecognition: its later empty command list must not run the cancelled command.
    this.Closed = true;
    this.SpeechRecognitionService.StopRecognition();
    clearTimeout(this.CloseTimer);
    this.dialogRef.close();
  }

  private CloseDialog(): void {
    if (this.Closed) {
      return;
    }
    this.Closed = true;
    clearTimeout(this.CloseTimer);

    switch (this.data.commandType) {
      case VoiceCommand.SetBattery:
        this.SetBatteryLevel(this.BatteryValue());
        break;
      case VoiceCommand.SkipWP:
        if (this.navigationService.navigation) {
          this.navigationService.SkipNextWaypoint();
        }
        else {
          let waypoints = [...this.waypointService.getWaypointsParams()];
          waypoints.splice(1, 1);
          this.waypointService.setWaypointParams(waypoints);
        }
        break;
      case VoiceCommand.SkipCS:
        this.navigationService.SkipNextChargingStation();
        break;
      case VoiceCommand.StopNavigation:
        this.navigationService.ObservableChangeNavigation.next("exit");
        break;
      case VoiceCommand.SetWeight:
        this.SetWeight(this.WeightValue());
        break;
      case VoiceCommand.NavigateToHome: {
        const waypointsHome = [this.waypointService.getWaypointsParams()[0]];
        waypointsHome.push(new LatLng(this.data.homeAddress.Location.lat, this.data.homeAddress.Location.lng));
        this.waypointService.setWaypointParams(waypointsHome);
        break;
      }
      case VoiceCommand.NavigateToWork: {
        const waypointsWork = [this.waypointService.getWaypointsParams()[0]];
        waypointsWork.push(new LatLng(this.data.workAddress.Location.lat, this.data.workAddress.Location.lng));
        this.waypointService.setWaypointParams(waypointsWork);
        break;
      }
      case VoiceCommand.ReadEta:
        break;
      case VoiceCommand.ReadRemainingDist:
        break;
    }
    this.dialogRef.close();
  }

  private SetBatteryLevel(newBatteryLevel: number): void {
    this.vehicleService.setBattery(newBatteryLevel);
    this.loggerService.onBatteryChangeEvent(newBatteryLevel);
    this.vehicleService.ObservableBatteryChanged.next(newBatteryLevel);
  }

  private SetWeight(weightValue: number): void {
    this.vehicleService.setWeight(weightValue);
    this.loggerService.onWeightChangeEvent();
  }

  public showMetricMeter(m: number): boolean {
    if (m < 1000 && this.settingsService.getUnit().Distance == Unit.Metric) {
      return true;
    }
    else {
      return false;
    }
  }

  public showMetricKMeter(m: number): boolean {
    if (m >= 1000 && this.settingsService.getUnit().Distance == Unit.Metric) {
      return true;
    }
    else {
      return false;
    }
  }

  public showImperialYard(m: number): boolean {
    if (this.utilsService.mToYard(m) < 1760 && this.settingsService.getUnit().Distance == Unit.Imperial) {
      return true;
    }
    else {
      return false;
    }
  }

  public showImperialMile(m: number): boolean {
    if (this.utilsService.mToYard(m) >= 1760 && this.settingsService.getUnit().Distance == Unit.Imperial) {
      return true;
    }
    else {
      return false;
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.CloseTimer);
    for (let subscription of this.Subscriptions) {
      subscription.unsubscribe();
    }
  }
}
